import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { getEvents, getEventsAdmin, getSeries, getFinishedEvents } from '../services/api';
import EventCard from '../components/EventCard';
import Navbar from '../components/Navbar';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTheme } from '../context/ThemeContext';
import useWindowSize from '../hooks/useWindowSize';

function SeriesCard({ series }) {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const count = parseInt(series.upcoming_count);

  return (
    <div style={{ cursor: 'pointer', marginBottom: isMobile ? '1.5rem' : '0' }} onClick={() => navigate(`/coleccion/${series.slug}`)}>
      <div style={{ position: 'relative', width: '100%', aspectRatio: '2/3', marginBottom: '0.8rem' }}>
        <div style={{ position: 'absolute', inset: 0, transform: 'rotate(-4deg) scale(0.94)', borderRadius: '8px', backgroundColor: colors.bgTertiary, border: `1px solid ${colors.border}`, opacity: 0.5, zIndex: 1 }} />
        <div style={{ position: 'absolute', inset: 0, transform: 'rotate(3deg) scale(0.97)', borderRadius: '8px', backgroundColor: colors.bgTertiary, border: `1px solid ${colors.border}`, opacity: 0.7, zIndex: 2 }} />

        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: '8px', zIndex: 3, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
          {series.display_image ? (
            <img src={series.display_image} alt={series.name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', transition: 'transform 0.3s ease' }}
              onMouseOver={e => !isMobile && (e.target.style.transform = 'scale(1.05)')}
              onMouseOut={e => !isMobile && (e.target.style.transform = 'scale(1)')}
            />
          ) : (
            <div style={{ width: '100%', height: '100%', backgroundColor: colors.bgTertiary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem' }}>🎵</div>
          )}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 45%)' }} />
          <div style={{ position: 'absolute', bottom: '0.7rem', left: '0.7rem', right: '0.7rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span style={{ backgroundColor: colors.accent, color: colors.accentText, fontSize: '0.68rem', fontWeight: 'bold', padding: '4px 10px', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              🗓️ {count} {count === 1 ? 'fecha' : 'fechas'}
            </span>
          </div>
        </div>
      </div>
      <div>
        <p style={{ color: colors.accent, fontSize: '0.72rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.3rem' }}>
          Colección
        </p>
        <h3 style={{ color: colors.text, fontSize: isMobile ? '1.1rem' : '1.05rem', fontWeight: 'bold', marginBottom: '0.3rem' }}>{series.name}</h3>
        <p style={{ color: colors.textSecondary, fontSize: '0.82rem', margin: 0 }}>
          {count === 1 ? 'Próxima edición disponible' : `${count} ediciones disponibles`}
        </p>
      </div>
    </div>
  );
}

function FinishedEventCard({ event }) {
  const { colors } = useTheme();
  const { width } = useWindowSize();
  const isMobile = width < 768;

  return (
    <div style={{ marginBottom: isMobile ? '1.5rem' : '0' }}>
      <div style={{ position: 'relative', width: '100%', aspectRatio: '2/3', marginBottom: '0.8rem', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
        {(event.image || event.banner_image) ? (
          <img
            src={event.image || event.banner_image}
            alt={event.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', filter: 'grayscale(60%) brightness(0.65)' }}
          />
        ) : (
          <div style={{ width: '100%', height: '100%', backgroundColor: colors.bgTertiary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem' }}>🎵</div>
        )}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 50%)' }} />
        <div style={{ position: 'absolute', top: '0.7rem', left: '0.7rem', display: 'flex', alignItems: 'center', gap: '0.3rem', backgroundColor: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.3)', color: '#ffffff', fontSize: '0.7rem', fontWeight: 'bold', padding: '4px 10px', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          <CheckCircleIcon style={{ fontSize: 14 }} /> Finalizado
        </div>
        <div style={{ position: 'absolute', bottom: '0.8rem', left: '0.8rem', right: '0.8rem' }}>
          <p style={{ color: '#ffffff', fontWeight: 'bold', fontSize: isMobile ? '1rem' : '0.95rem', margin: 0, textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>{event.name}</p>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem', margin: '0.2rem 0 0' }}>
            {new Date(event.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>
    </div>
  );
}

function Home() {
  const [events, setEvents] = useState([]);
  const [series, setSeries] = useState([]);
  const [finishedEvents, setFinishedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);
  const { colors } = useTheme();
  const { width } = useWindowSize();
  const isMobile = width < 768;

  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const fetchEvents = isAdmin ? getEventsAdmin : getEvents;
    Promise.all([fetchEvents(), getSeries(), getFinishedEvents()])
      .then(([eventsRes, seriesRes, finishedRes]) => {
        setEvents(eventsRes.data);
        setSeries(seriesRes.data);
        setFinishedEvents(finishedRes.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (events.length === 0) return;
    const timer = setInterval(() => {
      setCurrent(c => (c + 1) % events.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [events]);

  const prev = () => setCurrent(c => (c - 1 + events.length) % events.length);
  const next = () => setCurrent(c => (c + 1) % events.length);
  const featured = events[current];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.bg }}>
      <Helmet>
        <title>GaliTicket - Compra entradas para eventos en Galicia</title>
        <meta name="description" content="Compra entradas para los mejores eventos, conciertos y espectáculos de Galicia. Acceso rápido, seguro y con QR único por entrada." />
        <meta name="keywords" content="entradas Galicia, tickets eventos Galicia, conciertos Galicia, comprar entradas online, GaliTicket" />
        <meta property="og:title" content="GaliTicket - Compra entradas para eventos en Galicia" />
        <meta property="og:description" content="Compra entradas para los mejores eventos, conciertos y espectáculos de Galicia." />
        <meta property="og:image" content="https://galiticket.com/logo.png" />
        <meta property="og:url" content="https://galiticket.com" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="GaliTicket - Compra entradas para eventos en Galicia" />
        <meta name="twitter:description" content="Compra entradas para los mejores eventos, conciertos y espectáculos de Galicia." />
        <meta name="twitter:image" content="https://galiticket.com/logo.png" />
        <link rel="canonical" href="https://galiticket.com" />
      </Helmet>

      <Navbar />

      {!loading && events.length > 0 && (
        <div style={{
          position: 'relative',
          ...(isMobile ? { height: '220px' } : { height: 'min(56.25vw, 680px)' }),
          overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', inset: 0,
            backgroundSize: 'cover',
            backgroundPosition: 'top center',
            backgroundImage: featured?.banner_image
              ? `url(${featured.banner_image})`
              : (featured?.image ? `url(${featured.image})` : 'none'),
            backgroundColor: featured?.image ? 'transparent' : colors.bgTertiary,
          }} />
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.85) 30%, rgba(0,0,0,0.2) 65%, transparent 100%)',
          }} />

          <div style={{
            position: 'relative', zIndex: 1,
            padding: isMobile ? '0 1.2rem 1.5rem' : '0 3rem 2.5rem',
            height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
          }}>
            {isMobile ? (
              <>
                <h1 style={{
                  color: '#ffffff', fontSize: '1.5rem', fontWeight: 'bold',
                  lineHeight: 1.1, marginBottom: '0.6rem', textTransform: 'uppercase',
                  textShadow: '0 2px 8px rgba(0,0,0,1), 0 4px 20px rgba(0,0,0,0.9)',
                }}>
                  {featured?.name}
                </h1>
                {isAdmin && !featured?.published && (
                  <span style={{ backgroundColor: '#ff444422', color: '#ff4444', fontSize: '0.72rem', padding: '2px 8px', borderRadius: '20px', marginBottom: '0.5rem', display: 'inline-block', width: 'fit-content' }}>
                    Borrador — solo visible para admin
                  </span>
                )}
                <button style={{
                  backgroundColor: colors.accent, color: colors.accentText, border: 'none',
                  padding: '0.5rem 1.5rem', borderRadius: '4px', cursor: 'pointer',
                  fontWeight: 'bold', fontSize: '0.85rem', width: 'fit-content', textTransform: 'uppercase',
                }} onClick={() => window.location.href = `/${featured?.slug || featured?.id}`}>
                  Entradas
                </button>
              </>
            ) : (
              <>
                <h1 style={{
                  color: '#ffffff', fontSize: '2.8rem', fontWeight: 'bold',
                  lineHeight: 1.1, marginBottom: '0.4rem', textTransform: 'uppercase',
                  letterSpacing: '0.02em',
                  textShadow: '0 2px 10px rgba(0,0,0,1), 0 4px 30px rgba(0,0,0,0.9), 0 0 60px rgba(0,0,0,0.7)',
                }}>
                  {featured?.name}
                </h1>
                {isAdmin && !featured?.published && (
                  <span style={{ backgroundColor: '#ff444422', color: '#ff4444', fontSize: '0.75rem', padding: '2px 10px', borderRadius: '20px', marginBottom: '0.5rem', display: 'inline-block', width: 'fit-content' }}>
                    Borrador — solo visible para admin
                  </span>
                )}
                <p style={{
                  color: 'rgba(255,255,255,0.95)', fontSize: '0.95rem', marginBottom: '1rem',
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                  textShadow: '0 1px 8px rgba(0,0,0,1), 0 2px 16px rgba(0,0,0,0.9)',
                }}>
                  {featured && new Date(featured.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })} · {featured?.location}
                </p>
                <button style={{
                  backgroundColor: colors.accent, color: colors.accentText, border: 'none',
                  padding: '0.7rem 2rem', borderRadius: '4px', cursor: 'pointer',
                  fontWeight: 'bold', fontSize: '0.9rem', width: 'fit-content',
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                }} onClick={() => window.location.href = `/${featured?.slug || featured?.id}`}>
                  Entradas
                </button>
              </>
            )}
          </div>

          {!isMobile && (
            <>
              <button style={{ position: 'absolute', left: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', border: `1px solid ${colors.border}`, color: '#ffffff', borderRadius: '50%', width: '44px', height: '44px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }} onClick={prev}>
                <ArrowBackIosIcon style={{ fontSize: 18 }} />
              </button>
              <button style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', border: `1px solid ${colors.border}`, color: '#ffffff', borderRadius: '50%', width: '44px', height: '44px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }} onClick={next}>
                <ArrowForwardIosIcon style={{ fontSize: 18 }} />
              </button>
            </>
          )}

          <div style={{ position: 'absolute', bottom: '0.8rem', right: '1rem', display: 'flex', gap: '0.4rem', zIndex: 2 }}>
            {events.map((_, i) => (
              <div key={i} style={{ width: '6px', height: '6px', borderRadius: '50%', cursor: 'pointer', backgroundColor: i === current ? '#ffffff' : 'rgba(255,255,255,0.4)' }} onClick={() => setCurrent(i)} />
            ))}
          </div>
        </div>
      )}

      <div style={{ padding: isMobile ? '1.5rem 1rem' : '3rem 2rem' }}>
        {!loading && events.length === 0 && series.length === 0 && (
          <div style={{ marginBottom: isMobile ? '2rem' : '3rem' }}>
            <h2 style={{ color: colors.text, fontSize: isMobile ? '1.3rem' : '1.6rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              Actualmente no hay eventos en venta
            </h2>
            <p style={{ color: colors.textSecondary, fontSize: '0.9rem' }}>
              Estamos preparando la próxima temporada. Aquí abajo puedes ver los eventos que ya hemos organizado en GaliTicket.
            </p>
          </div>
        )}

        {(events.length > 0 || series.length > 0) && (
          <>
            <h2 style={{ color: colors.text, fontSize: isMobile ? '1.4rem' : '1.8rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>Todos los eventos</h2>
            {loading ? (
              <p style={{ color: colors.textSecondary }}>Cargando eventos...</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: isMobile ? '0' : '1.5rem' }}>
                {series.map(s => (
                  <SeriesCard key={`series-${s.id}`} series={s} />
                ))}
                {events.map(event => (
                  <div key={event.id} style={{ position: 'relative' }}>
                    {isAdmin && !event.published && (
                      <div style={{ position: 'absolute', top: '0.5rem', left: '0.5rem', zIndex: 10, backgroundColor: '#ff444422', border: '1px solid #ff4444', color: '#ff4444', fontSize: '0.7rem', padding: '2px 8px', borderRadius: '20px' }}>
                        Borrador
                      </div>
                    )}
                    <EventCard key={event.id} event={event} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {!loading && finishedEvents.length > 0 && (
          <div style={{ marginTop: (events.length > 0 || series.length > 0) ? (isMobile ? '2.5rem' : '3.5rem') : 0 }}>
            <h2 style={{ color: colors.text, fontSize: isMobile ? '1.3rem' : '1.6rem', fontWeight: 'bold', marginBottom: '0.4rem' }}>Eventos realizados</h2>
            <p style={{ color: colors.textSecondary, fontSize: '0.85rem', marginBottom: '1.5rem' }}>
              Algunos de los eventos que ya hemos organizado en GaliTicket.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: isMobile ? '0' : '1.5rem' }}>
              {finishedEvents.map(event => (
                <FinishedEventCard key={`finished-${event.id}`} event={event} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Home;